/**
 * Bridge's UI module.
 *
 * What Bridge says that the others do not: contracts are a level and a
 * denomination, possibly doubled; the side needs six plus the level in
 * tricks; vulnerability changes what every result is worth; and one hand is
 * dummy — face up from the opening lead and played by declarer, which the
 * table screen shows as an open hand declarer taps to play from.
 */
import type { ReactNode } from 'react'
import {
  bridgeRanking,
  vulnerability,
  type BridgeConfig,
  type BridgeContract,
  type RankingContext,
  type Seat,
} from '@cards/shared'
import type { GameUi, LegalCall } from '../types'
import type { TableView } from '../../table/view'
import { Strainy } from '../../table/atoms'
import {
  CardWord, auctionCallsOf, auctionStripOf, handHint, readCalls, seatStatus, sortBySuit, winningSeat,
} from '../common'
import { BridgeBids } from './BridgeBids'

const TRICKS = 13
const isRecord = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null

function contractOf(view: TableView): BridgeContract | null {
  const c = view.hand?.contract
  return isRecord(c) && typeof c.declarer === 'number' && typeof c.level === 'number' ? (c as unknown as BridgeContract) : null
}

function dummyOf(view: TableView): Seat | null {
  const c = contractOf(view)
  return c ? (c.declarer + 2) % view.seatCount : null
}

function configOf(view: TableView): BridgeConfig {
  const raw = (view.match?.config ?? view.snapshot.room.config ?? {}) as Partial<BridgeConfig>
  return { boards: raw.boards ?? 4, vulnerability: raw.vulnerability ?? 'chicago' }
}

/** Is this viewer's side vulnerable, and the other? */
function vul(view: TableView): { us: boolean; them: boolean } | null {
  const hand = view.hand
  if (!hand || view.myTeam === null) return null
  const v = vulnerability(hand.index, hand.dealer, view.seatCount, configOf(view))
  return { us: v[view.myTeam] ?? false, them: v[1 - view.myTeam] ?? false }
}

function vulWord(view: TableView): string {
  const v = vul(view)
  if (!v) return ''
  return v.us && v.them ? 'all vulnerable' : v.us ? 'you are vulnerable' : v.them ? 'they are vulnerable' : 'nobody vulnerable'
}

const contexts = new WeakMap<object, RankingContext>()
function rankingFor(view: TableView): RankingContext {
  const key = view.hand as object | null
  const hit = key ? contexts.get(key) : undefined
  if (hit) return hit
  const ctx = bridgeRanking(contractOf(view)?.strain ?? null)
  if (key) contexts.set(key, ctx)
  return ctx
}

/** `4♠ X`, `3NT` — the module's own label, drawn with a coloured glyph. */
function LabelText({ label }: { label: string }): JSX.Element {
  const m = /^(\d)(NT|[♠♥♦♣])(.*)$/.exec(label)
  if (!m) return <span>{label}</span>
  const strain = m[2] === 'NT' ? 'NT' : ({ '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' } as const)[m[2] as '♠']
  return (
    <span>
      {m[1]}
      <Strainy strain={strain} />
      {m[3]}
    </span>
  )
}

function callLabel(call: unknown): { label: ReactNode; pass: boolean } {
  if (!isRecord(call)) return { label: '?', pass: false }
  if (call.type === 'pass') return { label: 'pass', pass: true }
  if (call.type === 'double') return { label: 'X', pass: false }
  if (call.type === 'redouble') return { label: 'XX', pass: false }
  return { label: <>{String(call.level)}<Strainy strain={String(call.strain)} glyphSize={16} /></>, pass: false }
}

function sideTricks(view: TableView, declarer: Seat): number {
  const team = view.match?.table.teamOf[declarer]
  return (view.hand?.tricks ?? []).filter((t) => t.winner !== null && view.match?.table.teamOf[t.winner] === team).length
}

export const bridgeUi: GameUi = {
  id: 'bridge',
  name: 'Bridge',
  rotation: 'clockwise',
  options: [
    { key: 'boards', label: 'Boards', choices: [4, 8, 12, 16].map((v) => ({ label: String(v), value: v })) },
    {
      key: 'vulnerability',
      label: 'Vulnerability',
      choices: [{ label: 'Chicago', value: 'chicago' }, { label: 'Duplicate', value: 'duplicate' }],
    },
  ],

  tricksPerHand: () => TRICKS,
  tricksNeeded: (match) => {
    const c = match?.hand?.contract as BridgeContract | null | undefined
    return c ? c.level + 6 : null
  },

  contractReading: (view) => {
    const hand = view.hand
    if (!hand) return null
    const contract = contractOf(view)
    if (contract) {
      return {
        node: (
          <>
            <LabelText label={contract.label} />
            <span className="who">{contract.declarer === view.mySeat ? 'You' : view.name(contract.declarer)}</span>
          </>
        ),
        caption: vulWord(view),
      }
    }
    const auction = hand.auction
    if (isRecord(auction) && isRecord(auction.last)) {
      const last = auction.last as { seat: Seat; level: number; strain: string }
      return {
        node: (
          <>
            <span>{last.level}<Strainy strain={last.strain} /></span>
            <span className="who">{last.seat === view.mySeat ? 'You' : view.name(last.seat)}</span>
          </>
        ),
        caption: 'bid to beat',
      }
    }
    return { node: <span className="faint">—</span>, caption: hand.phase === 'abandoned' ? 'passed out' : vulWord(view) }
  },

  trackCaption: (view) => {
    const hand = view.hand
    const contract = contractOf(view)
    if (!hand || hand.phase === 'abandoned') return hand ? 'passed out' : ''
    if (!contract) return `${TRICKS} tricks to play`
    const need = contract.level + 6
    const taken = sideTricks(view, contract.declarer)
    const ours = view.match?.table.teamOf[contract.declarer] === view.myTeam
    if (hand.phase === 'handComplete') return `${ours ? 'took' : 'they took'} ${taken}, needed ${need}`
    return ours ? `${taken} of ${need}` : `they have ${taken} of ${need}`
  },

  seatStatus: (view, seat) => {
    const contract = contractOf(view)
    const extra: ReactNode[] = []
    if (contract?.declarer === seat) extra.push('declarer')
    if (dummyOf(view) === seat) extra.push('dummy')
    return seatStatus(view, seat, extra)
  },

  winningSeat,

  callout: (view) => {
    const trick = view.trick
    if (!trick || trick.plays.length === 0) return null
    const leading = trick.winner ?? trick.winning
    const card = trick.plays.find((p) => p.seat === leading)?.card ?? null
    if (leading === null || card === null) return null
    const contract = contractOf(view)
    if (trick.winner !== null && contract) {
      const taken = sideTricks(view, contract.declarer)
      return (
        <>
          <b>{view.name(leading)}</b> takes it with <CardWord card={card} />, declarer {taken} of {contract.level + 6}
        </>
      )
    }
    return (
      <>
        <b>{view.name(leading)}</b> has it with <CardWord card={card} />
      </>
    )
  },

  handHint: (view) => {
    const dummy = dummyOf(view)
    const toAct = view.actions?.toAct ?? null
    if (view.isMyTurn && toAct !== null && toAct === dummy && toAct !== view.mySeat) {
      return (
        <>
          <b>Dummy to play:</b> tap a card in {view.name(dummy)}'s hand
        </>
      )
    }
    if (view.mySeat !== null && view.mySeat === dummy && view.hand?.phase === 'play') {
      return `You are dummy: ${view.name(contractOf(view)!.declarer)} plays your cards`
    }
    return handHint(view)
  },

  cardMarks: (view, card) => ({ trump: rankingFor(view).isTrump(card) }),

  sortHand: (view, cards) => sortBySuit(cards, rankingFor(view)),

  handResult: (view) => {
    const hand = view.hand
    const result = hand?.result
    const contract = contractOf(view)
    if (!contract || !result) {
      return hand?.phase === 'handComplete' || hand?.phase === 'abandoned'
        ? { headline: 'Passed out', sentence: 'Nobody bid. The board scores nothing, and the deal moves on.' }
        : null
    }
    const ours = view.match?.table.teamOf[contract.declarer] === view.myTeam
    const taken = sideTricks(view, contract.declarer)
    const need = contract.level + 6
    const points = Math.max(...result.deltas)
    const who = contract.declarer === view.mySeat ? 'You' : view.name(contract.declarer)
    if (result.contractMade) {
      const over = taken - need
      return {
        headline: ours ? 'Made it' : 'They made it',
        sentence: `${who} made ${contract.label}${over > 0 ? ` with ${over} over` : ''}, for ${points}.`,
      }
    }
    return {
      headline: ours ? `Down ${need - taken}` : `Set them ${need - taken}`,
      sentence: `${who} needed ${need} and took ${taken}: ${points} to the defence.`,
    }
  },

  contractLabelNode: (label) => <LabelText label={label} />,

  auctionRule: 'The auction ends when three pass after a bid. Declarer plays dummy too.',

  auctionStrip: (view) => {
    const auction = view.hand?.auction
    const high = isRecord(auction) && isRecord(auction.last) ? (auction.last.seat as Seat) : null
    return auctionStripOf(view, readCalls(auction), callLabel, high)
  },

  auctionCalls: (view) => {
    const auction = view.hand?.auction
    const high = isRecord(auction) && isRecord(auction.last) ? (auction.last.seat as Seat) : null
    return auctionCallsOf(view, readCalls(auction), callLabel, high)
  },

  isPass: (call: LegalCall) => isRecord(call.call) && call.call.type === 'pass',
  explainHeadline: (call) => <LabelText label={call.label} />,
  bidButtonLabel: (call) => {
    if (isRecord(call.call) && call.call.type === 'double') return <>Double</>
    if (isRecord(call.call) && call.call.type === 'redouble') return <>Redouble</>
    return <>Bid <LabelText label={call.label} /></>
  },

  BidLayout: BridgeBids,

  decisionLayout: () => 'sheet',
  decisionHint: () => null,
  decisionWaiting: () => 'to choose',
}

